import { useQueryClient } from '@tanstack/react-query'
import {
  v2AdminGetStartupOutreachContactsQueryKey,
  v2GetPortfolioOutreachStatusOptions,
} from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { cn } from '@workspace/ui/lib/utils'
import { useState } from 'react'
import { orderVariantsForDisplay } from '../../outreach/lib/message-variants'

// Engine response uses snake_case (Pydantic v2 default — fields aren't aliased).
export type OutreachInsightContact = {
  id: string
  name: string
  title?: string | null
  linkedin_url?: string | null
  scope?: string | null
  message?: {
    message_id: string
    hook: string
    body: string
    msg1?: string | null
    msg2?: string | null
    msg3?: string | null
    msg1_with_link?: string | null
    body_is_override: boolean
    total_length: number
    pushed_at?: string | null
    connection_accepted_at?: string | null
    first_reply_at?: string | null
    reply_text?: string | null
    reply_summary?: string | null
    rejection_reason?: string | null
    rejection_detail?: string | null
    call_scheduled_candidate?: boolean | null
    call_confirmed_at?: string | null
    call_dismissed_at?: string | null
  } | null
}

export type OutreachInsightItem = {
  investor_id: string
  contacts: OutreachInsightContact[]
}

export type OutreachInsightsPayload = {
  startup?: {
    shared_body?: string | null
    outreach_msg1?: string | null
    outreach_msg2?: string | null
    outreach_msg3?: string | null
    outreach_msg1_prompt_version?: string | null
    outreach_msg2_prompt_version?: string | null
    outreach_msg3_prompt_version?: string | null
  }
  items?: OutreachInsightItem[]
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function fmtDate(d: string | null | undefined): string {
  return d ? formatDate(d) : '—'
}

/** One stored version of a message, as the admin variants endpoint returns it. */
export interface OutreachVariantView {
  id: string
  arm: string
  text: string
  promptVersion: string | null
}

function VersionBadge({ version }: { version?: string | null }) {
  if (!version) return null
  const isCustom = version === 'custom'
  const isFallback = version === 'fallback'
  return (
    <span
      className={cn(
        'ml-2 rounded px-1 py-0 text-[9px] font-medium tabular-nums',
        isCustom
          ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
          : isFallback
            ? 'bg-destructive/15 text-destructive'
            : 'bg-primary/15 text-primary'
      )}
      title={
        isCustom
          ? 'Hand-edited — no longer tracks a prompt version'
          : `Langfuse prompt version ${version}`
      }
    >
      {isCustom || isFallback ? version : `v${version}`}
    </span>
  )
}

function MsgRow({
  label,
  value,
  version,
  variants,
  onRegenerate,
  regenerating,
}: {
  label: string
  value: string | null | undefined
  version?: string | null
  /** Every live version of this message, if loaded. */
  variants?: OutreachVariantView[]
  onRegenerate?: () => void
  regenerating?: boolean
}) {
  const [showVariants, setShowVariants] = useState(false)
  // The sent copy is one of the variants, so counting it again as "extra"
  // overstates what exists. Arms + control + custom is the whole set.
  const extraCount = variants ? variants.length : 0

  const regenBtn = onRegenerate ? (
    <button
      type="button"
      onClick={onRegenerate}
      disabled={regenerating}
      className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[9px] hover:bg-muted disabled:opacity-50"
      title={`Regenerate ${label}`}
    >
      {regenerating ? '…' : '↻'}
    </button>
  ) : null

  return (
    <div className="border-t border-border/40 py-2 first:border-t-0 first:pt-0">
      {/* Label and metadata own one line; the copy owns the next. Inlining them
          made every message a wall of text with the version buried mid-sentence. */}
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {value ? (
            <span className="text-[10px] tabular-nums text-muted-foreground/60">
              {value.length}c
            </span>
          ) : null}
          <VersionBadge version={version} />
          {extraCount > 1 && (
            <button
              type="button"
              onClick={() => setShowVariants((v) => !v)}
              className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[9px] hover:bg-muted"
            >
              {showVariants ? 'hide' : `${extraCount} versions`}
            </button>
          )}
          {regenBtn}
        </span>
      </div>

      {value ? (
        <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-foreground">{value}</p>
      ) : (
        <p className="text-[11px] italic text-muted-foreground/60">—</p>
      )}

      {showVariants && variants && (
        <div className="mt-2 space-y-1.5 border-l-2 border-border/60 pl-2.5">
          {orderVariantsForDisplay(variants).map((variant) => (
            <div key={variant.id}>
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'rounded px-1 py-0 text-[9px] font-medium',
                    variant.arm === 'custom'
                      ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                      : variant.arm === 'control'
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-primary/15 text-primary'
                  )}
                >
                  {variant.arm}
                </span>
                <span className="text-[10px] tabular-nums text-muted-foreground/60">
                  {variant.text.length}c
                </span>
                <VersionBadge version={variant.promptVersion} />
              </div>
              <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground">
                {variant.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Per-startup outreach sequence (msg1/msg2/msg3) — the same copy sent to every
 * lead, generated once when the founder enables the campaign. Shown above the
 * per-investor cards in the admin Investors tab. The data-room link is appended
 * to msg1 and msg2 at push time, so the link is not part of these values.
 *
 * When ``startupId`` is provided, each row exposes an admin-only regenerate
 * button that forces a single-field rerun against the latest Langfuse prompt
 * (POST /admin/outreach/startups/:id/messages/regenerate?fields=msgN).
 */
export function StartupOutreachMessages({
  msg1,
  msg2,
  msg3,
  msg1Version,
  msg2Version,
  msg3Version,
  startupId,
  variants,
}: {
  msg1?: string | null
  msg2?: string | null
  msg3?: string | null
  msg1Version?: string | null
  msg2Version?: string | null
  msg3Version?: string | null
  startupId?: string
  /** Every live version per field, from the admin variants endpoint. */
  variants?: Record<'msg1' | 'msg2' | 'msg3', OutreachVariantView[]>
}) {
  const queryClient = useQueryClient()
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null)
  if (!msg1 && !msg2 && !msg3) return null

  const regenerate = startupId
    ? async (field: 'msg1' | 'msg2' | 'msg3') => {
        if (regeneratingField) return
        setRegeneratingField(field)
        try {
          const base = import.meta.env.VITE_BACKEND_URL
          const res = await fetch(
            `${base}/api/v2/admin/outreach/startups/${startupId}/messages/regenerate?fields=${field}`,
            { method: 'POST', credentials: 'include' }
          )
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
          const outreachKey = v2AdminGetStartupOutreachContactsQueryKey({
            path: { id: startupId },
            query: { limit: 200 },
          })
          await queryClient.invalidateQueries({ queryKey: outreachKey })
          await queryClient.refetchQueries({ queryKey: outreachKey, type: 'active' })
        } catch (err) {
          console.error('[StartupOutreachMessages] regenerate failed', err)
        } finally {
          setRegeneratingField(null)
        }
      }
    : undefined

  return (
    <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Startup messages
        </div>
        <Badge variant="secondary" className="text-[9px]">
          sent to all leads
        </Badge>
      </div>
      <MsgRow
        label="msg1 · intro"
        value={msg1}
        version={msg1Version}
        variants={variants?.msg1}
        onRegenerate={regenerate ? () => regenerate('msg1') : undefined}
        regenerating={regeneratingField === 'msg1'}
      />
      <MsgRow
        label="msg2 · pitch + CTA (~150w)"
        value={msg2}
        version={msg2Version}
        variants={variants?.msg2}
        onRegenerate={regenerate ? () => regenerate('msg2') : undefined}
        regenerating={regeneratingField === 'msg2'}
      />
      <MsgRow
        label="msg3 · momentum"
        value={msg3}
        version={msg3Version}
        variants={variants?.msg3}
        onRegenerate={regenerate ? () => regenerate('msg3') : undefined}
        regenerating={regeneratingField === 'msg3'}
      />
    </div>
  )
}

/**
 * Per-startup portfolio outreach sequence (msg1/msg2/msg3). Shown in the
 * Portfolio Founders tab of the admin page. Mirrors StartupOutreachMessages
 * but calls the portfolio regenerate endpoint.
 */
export function PortfolioOutreachMessages({
  msg1,
  msg2,
  msg3,
  msg1Version,
  msg2Version,
  msg3Version,
  startupId,
  variants,
}: {
  msg1?: string | null
  msg2?: string | null
  msg3?: string | null
  msg1Version?: string | null
  msg2Version?: string | null
  msg3Version?: string | null
  startupId?: string
  /** Every live version per field, from the admin variants endpoint. */
  variants?: Record<'msg1' | 'msg2' | 'msg3', OutreachVariantView[]>
}) {
  const queryClient = useQueryClient()
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null)
  if (!msg1 && !msg2 && !msg3) return null

  const regenerate = startupId
    ? async (field: 'msg1' | 'msg2' | 'msg3') => {
        if (regeneratingField) return
        setRegeneratingField(field)
        try {
          const base = import.meta.env.VITE_BACKEND_URL
          const res = await fetch(
            `${base}/api/v2/admin/portfolio/startups/${startupId}/messages/regenerate?fields=${field}`,
            { method: 'POST', credentials: 'include' }
          )
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
          await queryClient.invalidateQueries({
            queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
          })
        } catch (err) {
          console.error('[PortfolioOutreachMessages] regenerate failed', err)
        } finally {
          setRegeneratingField(null)
        }
      }
    : undefined

  return (
    <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Portfolio messages
        </div>
        <Badge variant="secondary" className="text-[9px]">
          sent to all portfolio founders
        </Badge>
      </div>
      <MsgRow
        label="msg1 · connection request"
        value={msg1}
        version={msg1Version}
        variants={variants?.msg1}
        onRegenerate={regenerate ? () => regenerate('msg1') : undefined}
        regenerating={regeneratingField === 'msg1'}
      />
      <MsgRow
        label="msg2 · follow-up"
        value={msg2}
        version={msg2Version}
        variants={variants?.msg2}
        onRegenerate={regenerate ? () => regenerate('msg2') : undefined}
        regenerating={regeneratingField === 'msg2'}
      />
      <MsgRow
        label="msg3 · second follow-up"
        value={msg3}
        version={msg3Version}
        variants={variants?.msg3}
        onRegenerate={regenerate ? () => regenerate('msg3') : undefined}
        regenerating={regeneratingField === 'msg3'}
      />
    </div>
  )
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate tabular-nums text-foreground">{v}</span>
    </div>
  )
}

function OutcomesBlock({ m }: { m: NonNullable<OutreachInsightContact['message']> }) {
  const exhausted_at = (m as { exhausted_at?: string | null }).exhausted_at
  const hasAnySignal = Boolean(
    m.pushed_at ||
      m.connection_accepted_at ||
      m.first_reply_at ||
      exhausted_at ||
      m.reply_summary ||
      m.rejection_reason ||
      m.rejection_detail ||
      m.call_scheduled_candidate ||
      m.call_confirmed_at ||
      m.call_dismissed_at ||
      m.reply_text
  )
  return (
    <div className="space-y-1 rounded bg-background/60 px-2 py-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Outcomes
      </div>
      {!hasAnySignal ? (
        <div className="text-[11px] italic text-muted-foreground/60">(no outcomes)</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
            <Stat k="pushed_at" v={fmtDate(m.pushed_at)} />
            <Stat k="connection_accepted_at" v={fmtDate(m.connection_accepted_at)} />
            <Stat k="first_reply_at" v={fmtDate(m.first_reply_at)} />
            <Stat k="exhausted_at" v={fmtDate(exhausted_at)} />
            <Stat k="reply_classification" v={m.reply_summary ? 'classified' : '—'} />
            <Stat k="rejection_reason" v={m.rejection_reason ?? '—'} />
            <Stat k="call_candidate" v={m.call_scheduled_candidate ? 'TRUE' : 'false'} />
            <Stat k="call_confirmed_at" v={fmtDate(m.call_confirmed_at)} />
            <Stat k="call_dismissed_at" v={fmtDate(m.call_dismissed_at)} />
          </div>
          {m.reply_summary && (
            <div className="rounded bg-muted/40 px-2 py-1 text-[11px]">
              <span className="text-muted-foreground">summary:</span>{' '}
              <span className="text-foreground">{m.reply_summary}</span>
            </div>
          )}
          {m.rejection_detail && (
            <div className="text-[11px]">
              <span className="text-muted-foreground">rejection detail:</span>{' '}
              <span className="text-foreground">{m.rejection_detail}</span>
            </div>
          )}
          {m.reply_text && !m.reply_summary && (
            <div className="rounded bg-muted/40 px-2 py-1 text-[11px]">
              <span className="text-muted-foreground">raw reply:</span>{' '}
              <span className="text-foreground">{m.reply_text}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-1 pt-1">
            {m.call_scheduled_candidate && !m.call_confirmed_at && !m.call_dismissed_at && (
              <Badge variant="warning" size="sm">
                LLM flagged · admin must decide
              </Badge>
            )}
            {m.call_confirmed_at && (
              <Badge variant="success" size="sm">
                call confirmed
              </Badge>
            )}
            {m.call_dismissed_at && (
              <Badge variant="outline" size="sm">
                call dismissed
              </Badge>
            )}
            {m.rejection_reason && (
              <Badge variant="destructive" size="sm">
                rejected · {m.rejection_reason}
              </Badge>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export function OutreachInsights({
  contacts,
  sharedBody,
  isLoading,
  startupId,
}: {
  contacts: OutreachInsightContact[]
  sharedBody: string | null
  isLoading: boolean
  startupId: string
}) {
  const [open, setOpen] = useState(false)
  if (isLoading) return null
  if (contacts.length === 0) {
    // No contacts scraped yet — admin should run Activity Research first.
    return (
      <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
        <span className="font-medium uppercase tracking-wide">Outreach</span>{' '}
        <span>contacts not scraped yet — run Activity Research above</span>
      </div>
    )
  }
  const generated = contacts.filter((c) => c.message)
  const pushed = generated.filter((c) => c.message?.pushed_at).length
  const replied = generated.filter((c) => c.message?.first_reply_at).length
  const accepted = generated.filter((c) => c.message?.connection_accepted_at).length

  return (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-[11px] text-muted-foreground hover:text-foreground"
      >
        <span className="flex items-center gap-2">
          <span className="font-medium uppercase tracking-wide">Outreach</span>
          <span className="tabular-nums">
            {generated.length}/{contacts.length} generated
          </span>
          {generated.length === 0 && (
            <span className="italic">— click Export JSON or unlock to generate</span>
          )}
          {pushed > 0 && (
            <Badge variant="secondary" size="sm">
              {pushed} sent
            </Badge>
          )}
          {accepted > 0 && (
            <Badge variant="success" size="sm">
              {accepted} accepted
            </Badge>
          )}
          {replied > 0 && (
            <Badge variant="success" size="sm">
              {replied} replied
            </Badge>
          )}
        </span>
        <span>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {contacts.map((c) => (
            <OutreachContactCard
              key={c.id}
              contact={c}
              sharedBody={sharedBody}
              startupId={startupId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function OutreachContactCard({
  contact,
  sharedBody,
  startupId,
}: {
  contact: OutreachInsightContact
  sharedBody: string | null
  startupId: string
}) {
  const m = contact.message
  const queryClient = useQueryClient()
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null)
  const messageId = m?.message_id
  const regenerateFields = async (fields: string) => {
    if (!messageId || regeneratingField) return
    setRegeneratingField(fields)
    try {
      const base = import.meta.env.VITE_BACKEND_URL
      const res = await fetch(
        `${base}/api/v2/admin/outreach/messages/${messageId}/regenerate?fields=${fields}`,
        { method: 'POST', credentials: 'include' }
      )
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const outreachKey = v2AdminGetStartupOutreachContactsQueryKey({
        path: { id: startupId },
        query: { limit: 200 },
      })
      await queryClient.invalidateQueries({ queryKey: outreachKey })
      await queryClient.refetchQueries({ queryKey: outreachKey, type: 'active' })
    } catch (err) {
      console.error('[OutreachContactCard] regenerate failed', err)
    } finally {
      setRegeneratingField(null)
    }
  }
  return (
    <div className="rounded-md border border-border bg-background/40 px-3 py-2 text-[11px]">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 font-medium text-foreground">
          <span className="truncate">{contact.name}</span>
          {contact.title ? (
            <span className="ml-1 font-normal text-muted-foreground">· {contact.title}</span>
          ) : null}
          {contact.scope ? (
            <div className="mt-0.5 text-[10px] font-normal italic text-muted-foreground">
              scope: {contact.scope}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {m ? (
            <>
              {m.pushed_at && (
                <Badge variant="secondary" size="sm" title={`pushed ${formatDate(m.pushed_at)}`}>
                  sent
                </Badge>
              )}
              {m.connection_accepted_at && (
                <Badge variant="success" size="sm">
                  accepted
                </Badge>
              )}
              {m.first_reply_at && (
                <Badge variant="success" size="sm">
                  replied
                </Badge>
              )}
            </>
          ) : (
            <Badge variant="outline" size="sm">
              not generated
            </Badge>
          )}
        </div>
      </div>
      {m && (
        <div className="mt-2 space-y-2">
          {/* Messages section */}
          <div className="space-y-1 rounded bg-background/60 px-2 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Messages
            </div>
            <MsgRow label="hook" value={m.hook} />
            <MsgRow label="body" value={m.body} />
            <MsgRow
              label="msg1 · intro (raw)"
              value={m.msg1}
              onRegenerate={() => regenerateFields('msg1')}
              regenerating={regeneratingField === 'msg1'}
            />
            <MsgRow label="msg1 · sent to lead (with link)" value={m.msg1_with_link} />
            <MsgRow
              label="msg2 · pitch (raw, ~150w)"
              value={m.msg2}
              onRegenerate={() => regenerateFields('msg2')}
              regenerating={regeneratingField === 'msg2'}
            />
            <MsgRow
              label="msg3 · momentum (sent as-is)"
              value={m.msg3}
              onRegenerate={() => regenerateFields('msg3')}
              regenerating={regeneratingField === 'msg3'}
            />
            {m.body_is_override && sharedBody && (
              <div className="text-[10px] italic text-muted-foreground">
                shared body: <span>{sharedBody}</span>
              </div>
            )}
          </div>

          {/* Outcomes — always show header; collapse content when no signal */}
          <OutcomesBlock m={m} />

          {/* Identifier */}
          <div className="text-[10px] text-muted-foreground/60">
            message_id: <span className="font-mono">{m.message_id}</span>
            {' · '}prompt v{(m as { prompt_version?: string | number }).prompt_version ?? '?'}
          </div>
        </div>
      )}
    </div>
  )
}
